import { useCallback, useEffect, useRef, useState } from 'react';
import { validateConductRiskReport } from '../functionApi';
import { useAxiosInterceptor } from '../wrappers';
import type {
  ConductRiskValidationResponse,
  ConductRiskValidationStatus,
  ConductRiskValidationIssue,
  ConductRiskDocumentType,
} from '../apiTypesConductRisk';

const POLLING_INTERVAL_MS = 60_000; // 60s come da direttive del backend (parser lento)

interface UseConductRiskValidationResult {
  status:                  ConductRiskValidationStatus | null;
  issuesByDocumentType:    Partial<Record<ConductRiskDocumentType, ConductRiskValidationIssue[]>>;
  loading:                 boolean;
  error:                   string | null;
  /** Forza una nuova chiamata a /validate (utile dopo re-upload). */
  refetch:                 () => Promise<void>;
}

/**
 * Chiama POST /reports/conduct_risk/{id}/validate all'apertura del report
 * e, se status = "NOT OK", fa polling ogni POLLING_INTERVAL_MS finche' non
 * ritorna OK o il componente viene smontato. Quando status === "OK" il
 * polling si ferma da solo.
 */
export const useConductRiskValidation = (
  reportId: string | undefined,
): UseConductRiskValidationResult => {
  // Registra il bearer token PRIMA delle fetch: gli useEffect di questo hook
  // e dei child eseguono bottom-up, quindi altrimenti la prima chiamata
  // parte senza Authorization e va in 401.
  useAxiosInterceptor();
  const [status,   setStatus]   = useState<ConductRiskValidationStatus | null>(null);
  const [issues,   setIssues]   = useState<Partial<Record<ConductRiskDocumentType, ConductRiskValidationIssue[]>>>({});
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);

  const validateOnce = useCallback(async (): Promise<ConductRiskValidationResponse | null> => {
    if (!reportId) return null;
    try {
      const resp = await validateConductRiskReport(reportId);
      if (cancelledRef.current) return null;
      setStatus(resp.status);
      setIssues(resp.issues_by_document_type ?? {});
      setError(null);
      return resp;
    } catch (err: any) {
      if (cancelledRef.current) return null;
      setError(err?.message ?? 'Errore nella validazione del report');
      return null;
    }
  }, [reportId]);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      await validateOnce();
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, [validateOnce]);

  useEffect(() => {
    cancelledRef.current = false;
    if (!reportId) return;

    let localTimer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      const resp = await validateOnce();
      if (cancelledRef.current) return;
      // Continua il polling solo finche' status !== 'OK'. Se assente (errore di
      // rete) riprova comunque per non lasciare la UI bloccata.
      if (resp?.status !== 'OK') {
        localTimer = setTimeout(tick, POLLING_INTERVAL_MS);
        timerRef.current = localTimer;
      }
    };

    setLoading(true);
    tick().finally(() => {
      if (!cancelledRef.current) setLoading(false);
    });

    return () => {
      cancelledRef.current = true;
      if (localTimer) clearTimeout(localTimer);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [reportId, validateOnce]);

  return { status, issuesByDocumentType: issues, loading, error, refetch };
};
